import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchRegistrationExamList } from 'src/app/modules/Recruitment/network/license-exam-crud';
import {
  RegistrationExamListItem,
  RegistrationExamListResponse,
} from 'src/app/modules/Recruitment/types/license-exam-types';
import { getConfig } from 'src/app/i18n';
import { useTranslation } from '@pruforce/common-adminweb-sdk';
import moment from 'moment';

require('moment/locale/zh-hk');

moment.updateLocale('zh-hk', {
  meridiem: function (hour, minute, isLower) {
    const hm = hour * 100 + minute;
    if (hm < 1200) {
      return '上午';
    } else {
      return '下午';
    }
  },
});

type RegistrationExamListItemWithDate = RegistrationExamListItem & {
  year?: number | '';
  month?: number | '';
};

export interface ExamSessionOption extends RegistrationExamListItem {
  value: string;
  label: string;
  dateText: string;
  timeText: string;
  year?: number | '';
  month?: number | '';
}

interface UseExamSessionSelectionParams {
  open: boolean;
  examType: string;
  examCode: string;
  examCentre: string;
  examineeId: string;
  selectedSession: ExamSessionOption | null;
}

const SCHEDULE_DATE_TIME_FORMAT = {
  START_DATE: 'recruitment.exam.registration.adminRegistration.sessionSelection.start_date_format',
  START_TIME: 'recruitment.exam.registration.adminRegistration.sessionSelection.start_time_format',
  END_TIME: 'recruitment.exam.registration.adminRegistration.sessionSelection.end_time_format',
} as const;

const mapSessionOption = ({
  item,
  locale,
  t,
}: {
  item: RegistrationExamListItemWithDate;
  locale: string;
  t: any;
}): ExamSessionOption => {
  const DATE_FORMAT = 'YYYY-MM-DD hh:mm';

  const startDateTime = moment(`${item.examDate} ${item.startTime}`, DATE_FORMAT).locale(locale);
  const endDateTime = moment(`${item.examDate} ${item.endTime}`, DATE_FORMAT).locale(locale);

  const dateText = startDateTime.format(t(SCHEDULE_DATE_TIME_FORMAT.START_DATE));

  const timeText = startDateTime
    .format(t(SCHEDULE_DATE_TIME_FORMAT.START_TIME))
    .replace('{{}}', endDateTime.format(t(SCHEDULE_DATE_TIME_FORMAT.END_TIME)))
    .replaceAll('00分', '');

  return {
    ...item,
    value: item.scheduleId,
    label: `${dateText} ${timeText}`,
    dateText,
    timeText,
  };
};

const getDefaultValue = (values: number[], currentValue: number) => {
  if (!values.length) {
    return undefined;
  }
  if (values.includes(currentValue)) {
    return currentValue;
  }
  const previousValue = values.filter((value) => value < currentValue).sort((a, b) => b - a)[0];
  return previousValue ?? Math.min(...values);
};

const getDefaultYearMonth = (
  list: RegistrationExamListResponse['dropdownList'] = [],
): {
  year: number | '';
  month: number | '';
} => {
  const now = moment();
  const yearsWithMonths = list.filter(({ months }) => months.length > 0);
  const defaultYear = getDefaultValue(
    yearsWithMonths.map(({ year }) => year),
    now.year(),
  );

  if (defaultYear === undefined) {
    return {
      year: '',
      month: '',
    };
  }

  const selectedYearOption = yearsWithMonths.find(({ year }) => year === defaultYear);
  return {
    year: defaultYear,
    month: getDefaultValue(selectedYearOption?.months ?? [], now.month() + 1) ?? '',
  };
};

const getInitialYearMonthCandidates = (
  list: RegistrationExamListResponse['dropdownList'],
  selectedSession: ExamSessionOption | null,
): Array<{ year: number; month: number }> => {
  const { year: defaultYear, month: defaultMonth } = getDefaultYearMonth(list);
  let initialYear = defaultYear;
  let initialMonth = defaultMonth;
  const selectedYear = selectedSession?.year;
  const selectedMonth = selectedSession?.month;

  if (
    typeof selectedYear === 'number' &&
    typeof selectedMonth === 'number' &&
    list.some(({ year, months }) => year === selectedYear && months.includes(selectedMonth))
  ) {
    initialYear = selectedYear;
    initialMonth = selectedMonth;
  }

  if (typeof initialYear !== 'number' || typeof initialMonth !== 'number') {
    return [];
  }

  const candidateYear = initialYear;
  const candidateMonth = initialMonth;
  const months = list.find(({ year }) => year === candidateYear)?.months ?? [];
  return [
    ...months.filter((month) => month <= candidateMonth).sort((left, right) => right - left),
    ...months.filter((month) => month > candidateMonth).sort((left, right) => left - right),
  ].map((month) => ({ year: candidateYear, month }));
};

export const useExamSessionSelection = ({
  open,
  examType,
  examCode,
  examCentre,
  examineeId,
  selectedSession,
}: UseExamSessionSelectionParams) => {
  const t = useTranslation();
  const lang = getConfig().selectedLang;

  const [selectedYear, setSelectedYear] = useState<number | ''>(() =>
    typeof selectedSession?.year === 'number' ? selectedSession.year : moment().year(),
  );
  const [selectedMonth, setSelectedMonth] = useState<number | ''>(() =>
    typeof selectedSession?.month === 'number' ? selectedSession.month : moment().month() + 1,
  );
  const [pendingSession, setPendingSession] = useState<ExamSessionOption | null>(null);

  const [loading, setLoading] = useState(false);
  const [examListRaw, setExamListRaw] = useState<RegistrationExamListResponse | null>(null);

  const yearOptions = useMemo(
    () =>
      [...(examListRaw?.dropdownList ?? [])].filter(({ months }) => months.length > 0).sort((a, b) => b.year - a.year),
    [examListRaw?.dropdownList],
  );

  const monthOptions = useMemo(
    () => [...(yearOptions.find(({ year }) => year === selectedYear)?.months ?? [])].sort((a, b) => a - b),
    [yearOptions, selectedYear],
  );

  const examSessionOptions = useMemo(
    () =>
      (examListRaw?.examList ?? []).map((item) =>
        mapSessionOption({
          item: {
            ...item,
            year: selectedYear,
            month: selectedMonth,
          },
          locale: lang,
          t,
        }),
      ),
    [examListRaw?.examList, selectedYear, selectedMonth, lang, t],
  );

  const loadExamSessions = useCallback(
    async (
      year?: number,
      month?: number,
      findInitialAvailableMonth = false,
      fallbackCandidates?: Array<{ year: number; month: number }>,
    ) => {
      setLoading(true);

      try {
        const params = {
          examType,
          examCode,
          examCentre,
          examineeId,
          ...(year !== undefined && { year }),
          ...(month !== undefined && { month }),
        };
        const rawResp = await fetchRegistrationExamList(params);

        if (findInitialAvailableMonth || fallbackCandidates) {
          if (rawResp.examList.length > 0) {
            setExamListRaw(rawResp);
            return;
          }

          const refreshedYearMonths = rawResp.dropdownList?.find((item) => item.year === year)?.months;
          const sessions =
            fallbackCandidates && typeof year === 'number'
              ? (refreshedYearMonths ?? fallbackCandidates.map(({ month }) => month))
                  .map((sessionMonth) => ({ year, month: sessionMonth }))
                  .sort((left, right) => right.month - left.month)
              : getInitialYearMonthCandidates(rawResp.dropdownList ?? [], selectedSession);
          let matchedResponse: RegistrationExamListResponse | null = null;

          for (const session of sessions) {
            if (session.year === year && session.month === month) {
              continue;
            }

            const candidateResponse = await fetchRegistrationExamList({
              examType,
              examCode,
              examCentre,
              examineeId,
              year: session.year,
              month: session.month,
            });

            if (candidateResponse.examList.length > 0) {
              setSelectedYear(session.year);
              setSelectedMonth(session.month);
              matchedResponse = candidateResponse;
              break;
            }
          }

          if (matchedResponse) {
            setExamListRaw(matchedResponse);
          } else {
            const searchedYear = sessions[0]?.year ?? year;
            setExamListRaw({
              ...rawResp,
              examList: [],
            });
            if (!fallbackCandidates) {
              setSelectedYear(sessions.length > 0 ? searchedYear ?? '' : '');
            }
            setSelectedMonth('');
          }
          return;
        }

        setExamListRaw(rawResp);
      } catch (e) {
        setExamListRaw(null);
      } finally {
        setLoading(false);
      }
    },
    [examType, examCode, examCentre, examineeId, selectedSession],
  );

  const handleYearChange = (year: number) => {
    if (loading) {
      return;
    }
    const availableMonths = [...(yearOptions.find((item) => item.year === year)?.months ?? [])].sort(
      (left, right) => right - left,
    );
    const monthCandidates = availableMonths.map((month) => ({ year, month }));
    const defaultMonth = monthCandidates[0]?.month;

    setSelectedYear(year);
    setSelectedMonth(defaultMonth ?? '');
    setPendingSession(null);

    loadExamSessions(year, defaultMonth, false, monthCandidates);
  };

  const handleMonthChange = (month: number) => {
    if (loading || selectedYear === '') {
      return;
    }
    setSelectedMonth(month);
    setPendingSession(null);
    loadExamSessions(selectedYear, month);
  };

  useEffect(() => {
    if (!examType || !examCode || !examCentre || !examineeId) {
      setExamListRaw(null);
      setSelectedYear(moment().year());
      setSelectedMonth(moment().month() + 1);
      setPendingSession(null);
      return;
    }

    setExamListRaw(null);
    setSelectedYear(moment().year());
    setSelectedMonth(moment().month() + 1);
    setPendingSession(null);
  }, [examType, examCode, examCentre, examineeId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!examType || !examCode || !examCentre || !examineeId) {
      return;
    }

    if (examListRaw !== null) {
      return;
    }

    setPendingSession(selectedSession);
    loadExamSessions(
      selectedYear === '' ? undefined : selectedYear,
      selectedMonth === '' ? undefined : selectedMonth,
      true,
    );
  }, [open, examType, examCode, examCentre, examineeId, examListRaw, loadExamSessions, selectedYear, selectedMonth]);

  return {
    yearOptions,
    monthOptions,
    selectedYear,
    selectedMonth,
    loading,
    examSessionOptions,
    pendingSession,
    setPendingSession,
    handleYearChange,
    handleMonthChange,
  };
};
